import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth.store';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/pages/auth/LoginPage.vue'),
      meta: { guest: true },
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/pages/auth/RegisterPage.vue'),
      meta: { guest: true },
    },
    {
      path: '/register-company',
      name: 'register-company',
      component: () => import('@/pages/auth/RegisterCompanyPage.vue'),
      meta: { guest: true },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/pages/auth/ForgotPasswordPage.vue'),
      meta: { guest: true },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/pages/auth/ResetPasswordPage.vue'),
      meta: { guest: true },
    },
    {
      path: '/no-companies',
      name: 'no-companies',
      component: () => import('@/pages/auth/NoCompaniesPage.vue'),
      meta: { requiresAuth: true, noCompanyRequired: true },
    },
    {
      path: '/',
      component: () => import('@/layouts/DefaultLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('@/pages/dashboard/DashboardPage.vue'),
          meta: { roles: ['admin', 'hr', 'manager'] },
        },
        {
          path: 'projects',
          name: 'projects',
          component: () => import('@/pages/projects/ProjectsListPage.vue'),
        },
        {
          path: 'projects/:id',
          name: 'project-detail',
          component: () => import('@/pages/projects/ProjectDetailPage.vue'),
        },
        {
          path: 'clients',
          name: 'clients',
          component: () => import('@/pages/clients/ClientsListPage.vue'),
        },
        {
          path: 'invoices',
          name: 'invoices',
          component: () => import('@/pages/invoices/InvoicesListPage.vue'),
        },
        {
          path: 'estimates',
          name: 'estimates',
          component: () => import('@/pages/estimates/EstimatesListPage.vue'),
        },
        {
          path: 'expenses',
          name: 'expenses',
          component: () => import('@/pages/expenses/ExpensesListPage.vue'),
        },
        {
          path: 'timesheets',
          name: 'timesheets',
          component: () => import('@/pages/timesheets/TimesheetsListPage.vue'),
        },
        {
          path: 'files',
          name: 'files',
          component: () => import('@/pages/files/FilesListPage.vue'),
        },
        {
          path: 'stock',
          name: 'stock',
          component: () => import('@/pages/stock/StockListPage.vue'),
        },
        {
          path: 'users',
          name: 'users',
          component: () => import('@/pages/company/CompanyUsersPage.vue'),
          meta: { roles: ['admin'] },
        },
        {
          path: 'profile',
          name: 'profile',
          component: () => import('@/pages/profile/ProfilePage.vue'),
        },
        {
          path: 'settings',
          name: 'settings',
          component: () => import('@/pages/company/CompanySettingsPage.vue'),
          meta: { roles: ['admin'] },
        },
        {
          path: 'billing/success',
          name: 'billing-success',
          redirect: { name: 'users', query: { billingSuccess: '1' } },
          meta: { roles: ['admin'] },
        },
        {
          path: 'billing/cancel',
          name: 'billing-cancel',
          redirect: { name: 'users', query: { billingCanceled: '1' } },
          meta: { roles: ['admin'] },
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/',
    },
  ],
});

router.beforeEach((to, _from, next) => {
  const authStore = useAuthStore();

  // Check if user is authenticated
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next('/login');
    return;
  }

  // Handle guest routes (login, register)
  if (to.meta.guest && authStore.isAuthenticated) {
    // If user has no companies, send to no-companies page
    if (authStore.companies.length === 0) {
      next('/no-companies');
      return;
    }
    // Redirect to appropriate landing page based on role
    const role = authStore.userRole;
    if (role === 'member') {
      next('/projects');
    } else {
      next('/');
    }
    return;
  }

  // Handle no-companies page
  if (to.path === '/no-companies') {
    if (!authStore.isAuthenticated) {
      next('/login');
      return;
    }
    // If user now has companies, redirect to dashboard
    if (authStore.companies.length > 0) {
      next('/');
      return;
    }
    next();
    return;
  }

  // Check if authenticated user has companies (required for all non-guest routes except /no-companies)
  if (authStore.isAuthenticated && authStore.companies.length === 0 && !to.meta.noCompanyRequired) {
    next('/no-companies');
    return;
  }

  // Ensure currentCompanyId is set if user has companies
  if (authStore.isAuthenticated && authStore.companies.length > 0 && !authStore.currentCompanyId && !to.meta.noCompanyRequired) {
    // Set to first company if not set
    authStore.setCurrentCompany(authStore.companies[0].id);
  }

  // Check role-based access
  if (to.meta.roles && authStore.isAuthenticated) {
    const allowedRoles = to.meta.roles as string[];
    const userRole = authStore.userRole;
    if (userRole && allowedRoles.includes(userRole)) {
      next();
    } else {
      // Redirect member to projects, others to dashboard
      next(userRole === 'member' ? '/projects' : '/');
    }
    return;
  }

  next();
});

export default router;
